import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { ReactComponent as MergeIcon } from "@assets/icons/merge.svg";
import Loader from "@components/global/Loader/Loader";
import MinerFlag from "@components/global/new/MinerFlag/MinerFlag";
import Status from "@components/global/new/Status/Status";
import RejectedChart from "@components/global/RejectedChart";
import Turnstile, { TurnDirection } from "@components/global/Turnstile";
import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import { useRackSectionSchemeDrag } from "@mobx/hooks/useRackSectionSchemeDrag";
import { useWatchNetwork } from "@mobx/services/http";
import mainLocationsStore from "@mobx/store/locations";
import { ItemStore } from "@mobx/store/locations/interfaces";
import { IBackRackItemType3, IRackItem3, RACK_SECTION_TYPE3 } from "@mobx/store/locations/types";
import { EQUIPMENT_TYPE } from "@store/Equipments/actionTypes";
import { hexToRgb } from "@utils/hexToRgb";
import { parseMinerName } from "@utils/parseMinerName";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import BackIcon3 from "../BackIcon3";
import EmptyItem3 from "../EmptyItem3";
import MergeItem3 from "../MergeItem3";
import NullItem3 from "../NullItem3";
import styles from "./RackItem.module.scss";

enum ItemType {
  Null,
  Empty,
  Merged,
  Normal,
}

interface ItemInfo {
  id: number;
  title: string;
  name: string;
  status: MINER_STATUSES_E;
  userColor: string;
  unmerge?: boolean;
  backTypes?: IBackRackItemType3[];
  accepted?: number;
  rejected?: number;
  meta?: string;
}

export interface ILightRackItem3Props {
  itemStore: ItemStore;
}

const useControl = (props: ILightRackItem3Props) => {
  const item = props.itemStore.item as IRackItem3;
  const { isMerging, isLocked, isLoading } = props.itemStore;
  const locationStore = mainLocationsStore.activeLocation;
  const buildingStore = locationStore?.activeBuilding;
  const wasFlipped = useRef(buildingStore?.isFlipped);
  const [rotate, setRotate] = useState(buildingStore?.isFlipped);
  const [turn, setTurn] = useState(TurnDirection.None);
  const type = useMemo(
    () =>
      item.pos === RACK_SECTION_TYPE3.NULL
        ? ItemType.Null
        : item.pos === RACK_SECTION_TYPE3.EMPTY
          ? ItemType.Empty
          : (Object.hasOwn(item, "merged") && item.merged)
            ? ItemType.Merged
            : ItemType.Normal,
    [item]
  );
  const selectedUsers = useMemo(() => locationStore?.selectedUsers ?? new Set<number>, [locationStore?.selectedUsers]);
  const self = useRef<HTMLDivElement | null>(null);
  const schemeDragItem = useRackSectionSchemeDrag(props.itemStore, self);
  useWatchNetwork(props.itemStore);

  useEffect(() => {
    if (buildingStore?.isFlipped === wasFlipped.current) return;
    wasFlipped.current = buildingStore?.isFlipped;
    if (buildingStore?.isFlipped) {
      setTurn(TurnDirection.Left);
      setTimeout(() => {
        setRotate(true);
        setTurn(TurnDirection.None);
      }, 500);
    } else {
      setTurn(TurnDirection.Right);
      setTimeout(() => {
        setRotate(false);
        setTurn(TurnDirection.None);
      }, 500);
    }
  }, [buildingStore?.isFlipped]);

  const notFiltered = useMemo(() => {
    let filteredItem = false;
    if (item.pos === RACK_SECTION_TYPE3.MINER) {
      const miner = item as IRackItem3;
      filteredItem = selectedUsers.has(+miner.user_id);
    }
    return !filteredItem && selectedUsers.size > 0;
  }, [item, selectedUsers]);

  const info: ItemInfo | undefined = useMemo(() => {
    if (type !== ItemType.Normal) return undefined;
    const it = item as IRackItem3;
    const title = it.miner_name || it.miner_id.toString();
    const { sectionCoords, letter } = parseMinerName(title!);
    let name = "";
    if (!it.miner_name || !sectionCoords) name = title || "";
    else name = `${letter}${sectionCoords}`;
    return {
      title,
      name,
      status: it.status,
      userColor: it.user_color,
      unmerge: !!it.merge?.length,
      backTypes: it.types,
      accepted: it.accepted,
      rejected: it.rejected,
      id: it.id,
      meta: it.meta,
    };
  }, [item, type]);

  const currentColor = useMemo(() => {
    const primaryColor = "#0a28ab";
    const lightColor = "#fff";
    const it = item as IRackItem3;
    if (!it?.user_color) return primaryColor;
    const parsed = hexToRgb(it.user_color);
    if (parsed) {
      return (parsed.rgb.r + parsed.rgb.g + parsed.rgb.b) / 3 > 150
        ? primaryColor
        : lightColor;
    }
    return primaryColor;
  }, [(item as IRackItem3)?.user_color]);

  return {
    notFiltered,
    type,
    merging: isMerging,
    lock: isLocked,
    currentColor,
    rotate,
    turn,
    loading: isLoading,
    activeRackItemID: buildingStore?.activeRackItemId,
    item,
    self,
    isSelectedForSchemeDrag: schemeDragItem.isSelectedForSchemeDrag,
    dragID: schemeDragItem.dragID,
    ...info,
    mouseDown(e: React.MouseEvent<HTMLElement>) {
      if (!e.ctrlKey) return;
      schemeDragItem.mouseDown(e);
      e.stopPropagation();
      e.preventDefault();
    },
  };
};

const LightRackItem3 = observer((props: ILightRackItem3Props) => {
  const ctrl = useControl(props);
  const currentMeta = JSON.parse(ctrl.meta || "{}");
  const LightBack = () => (
    <div
      className={cn(styles.lightItemBack)}
      style={{
        borderColor: ctrl.unmerge ? ctrl.userColor : undefined,
      }}
      ref={ctrl.self}
      onMouseDownCapture={ctrl.mouseDown}
      data-drag-id={ctrl.dragID}
    >
      {ctrl.loading && <Loader size={24} />}

      <div className={styles.name}>
        <h5 title={ctrl.title} className={styles.title}>
          {ctrl.name}
        </h5>
      </div>
      <div className={styles.rotate_icons}>
        <BackIcon3 item={ctrl.item as IRackItem3} type={EQUIPMENT_TYPE.NETWORK} />
        <BackIcon3 item={ctrl.item as IRackItem3} type={EQUIPMENT_TYPE.ELECTRICAL} />
      </div>
      <div
        className={cn(styles.bottom)}
        style={{ backgroundColor: ctrl.userColor }}
      ></div>
    </div>
  );

  const LightFront = () => (
    <div
      tabIndex={0}
      className={cn(styles.container, styles.lightItem, {
        [styles.not_filtered]: ctrl.notFiltered,
        [styles.selected]: ctrl.activeRackItemID === ctrl.item.id,
        [styles.selectedForSchemeDrag]: ctrl.isSelectedForSchemeDrag,
      })}
      ref={ctrl.self}
      onMouseDownCapture={ctrl.mouseDown}
      data-drag-id={ctrl.dragID}
    >
      <div className={styles.name}>
        <h5 title={ctrl.title} className={styles.title}>
          {ctrl.name}
        </h5>
      </div>
      <div className={styles.middle}>
        <Status status={ctrl.status!} className={styles.status} crop />
        {(currentMeta.flagText || currentMeta.flagColor) && (
          <MinerFlag
            className={styles.flag}
            minerId={ctrl.id || 0}
            meta={currentMeta}
          />
        )}
        <RejectedChart
          rejected={ctrl.rejected}
          accepted={ctrl.accepted}
          className={styles.rejected}
        />
      </div>
      <div
        className={cn(styles.bottom)}
        style={{ backgroundColor: ctrl.userColor }}
      >
        <div className={cn(styles.block, styles.b1)}>
          {!ctrl.merging &&
            (ctrl.lock ? (
              <LockOutlined
                className={cn(styles.icon, styles.icon_lock)}
                style={{ color: ctrl.currentColor }}
                rev={undefined}
              />
            ) : (
              <UnlockOutlined
                className={cn(styles.icon, styles.icon_unlock)}
                style={{ color: ctrl.currentColor }}
                rev={undefined}
              />
            ))}
        </div>
        <div
          className={cn(styles.block, styles.b2, {
            [styles.unmerge]: ctrl.unmerge,
          })}
        >
          <MergeIcon
            className={styles.merge_icon}
            style={{ fill: ctrl.currentColor }}
          />
        </div>
      </div>
    </div>
  );

  return ctrl.type === ItemType.Null ? (
    <NullItem3 />
  ) : ctrl.type === ItemType.Empty ? (
    <EmptyItem3 itemStore={props.itemStore} />
  ) : ctrl.type === ItemType.Merged ? (
    <div
      className={cn(styles.container, {
        [styles.selectedForSchemeDrag]: ctrl.isSelectedForSchemeDrag,
      })}
      ref={ctrl.self}
      onMouseDownCapture={ctrl.mouseDown}
      data-drag-id={ctrl.dragID}
    >
      <MergeItem3 minerName={(props.itemStore.item as IRackItem3).miner_name} />
    </div>
  ) : ctrl.turn ? (
    <Turnstile left={LightFront} right={LightBack} dir={ctrl.turn} dur={500} />
  ) : ctrl.rotate ? (
    <LightBack />
  ) : (
    <LightFront />
  );
});

LightRackItem3.displayName = "LightRackItem3";

export default LightRackItem3;
